import { CloseButton, Group, Stack } from "@mantine/core";
import CreateModelEmail from "./emails/CreateModelEmail";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback } from "react";
import CreateModelLinkedin from "./linkedin/CreateModelLinkedin";
import PagePermission from "../../../components/hooks/usePagePermission";
import { getModelTypeProps } from "./modelUtils";
import { useLanguage } from "../../../stores/LanguageStore";
import PageTitle from "../../../components/View/PageTitle";
import CreateSignature from "../senders/signatures/CreateSignature";
import CreateModelSmv from "./smv/CreateModelSmv";
import CreateModelLinkedinVoice from "./linkedin/CreateModelLinkedinVoice";
import { useProfileStore } from "../../../stores/UserStore";

const ModelPageCreate = ({ is_ai }) => {
  const lang = useLanguage((s) => s.language);
  const { folderId, modelType } = useParams();
  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    if (folderId) {
      navigate(`/models/folder/${folderId}/model/none/${modelType}`);
    } else {
      navigate(`/models/${modelType}`);
    }
  }, [folderId, modelType, navigate]);

  const onCreate = useCallback(
    (newItem) => {
      if (folderId) {
        navigate(`/models/folder/${folderId}/model/${newItem.id}/${modelType}`);
      } else {
        navigate(`/models/${modelType}/${newItem.id}`);
      }
    },
    [folderId, modelType, navigate]
  );

  const getComponent = useCallback(() => {
    if (modelType === "email") {
      return (
        <CreateModelEmail
          onCreated={onCreate}
          is_ai={is_ai}
          handleClose={handleBack}
        />
      );
    }
    if (modelType === "email-signature") {
      return <CreateSignature onCreated={onCreate} handleClose={handleBack} />;
    }
    if (modelType === "linkedin-invitation") {
      return (
        <CreateModelLinkedin
          type={"linkedin_invitation"}
          onCreated={onCreate}
          handleClose={handleBack}
        />
      );
    }
    if (modelType === "linkedin-message") {
      return (
        <CreateModelLinkedin
          type={"linkedin_message"}
          onCreated={onCreate}
          maxChars={null}
          handleClose={handleBack}
        />
      );
    }
    if (modelType === "linkedin-voice-message") {
      return (
        <CreateModelLinkedinVoice
          onCreated={onCreate}
          handleClose={handleBack}
        />
      );
    }
    if (modelType === "sms") {
      return (
        <CreateModelLinkedin
          type={"sms"}
          onCreated={onCreate}
          maxChars={useProfileStore.getState().profile?.id === 1125 ? 9999 : 160}
          handleClose={handleBack}
        />
      );
    }
    if (modelType === "smv") {
      return <CreateModelSmv onCreated={onCreate} handleClose={handleBack} />;
    }
  }, [modelType, onCreate, is_ai, handleBack]);

  return (
    <Stack flex={1} gap={"lg"}>
      <PagePermission
        permission={getModelTypeProps(modelType)?.create_permission}
      />

      <Stack
        mx={"auto"}
        mt={32}
        p={16}
        mb={32}
        gap={"xs"}
        w={is_ai ? "60%" : undefined}
        style={{
          boxShadow: "#00000016 0 2px 6px",
          background: "#fff",
          borderRadius: 12,
          width: "90%",
        }}
        h={"calc(100vh - 128px)"}
      >
        <Group>
          <CloseButton size={40} onClick={handleBack} />

          <PageTitle
            title={`${lang.models.create_model}: ${is_ai
              ? lang.routes.models.prompt
              : getModelTypeProps(modelType)?.name
              }`}
          />
        </Group>
        {getComponent()}
      </Stack>
    </Stack>
  );
};

export default ModelPageCreate;
